<script setup>
/* global axios */
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const router = useRouter();
const { accountId } = useAccount();

// Shared state
const appId = ref('');
const appSecret = ref('');
const shortLivedToken = ref('');
const activeTab = ref('add'); // 'add' or 'tokens'

// Add pages state
const isFetching = ref(false);
const isConnecting = ref(false);
const pages = ref([]);
const selectedPages = ref({});
const userAccessToken = ref('');
const step = ref(1);

// Token management state
const isChecking = ref(false);
const isRenewing = ref(false);
const channels = ref([]);
const showRenewForm = ref(false);
const renewToken = ref('');

const apiBase = () => `/api/v1/accounts/${accountId.value}/facebook_app`;

// ━━━ Add Pages Logic ━━━
const fetchPages = async () => {
  if (!appId.value || !appSecret.value || !shortLivedToken.value) {
    useAlert(t('INBOX_MGMT.ADD.FACEBOOK_APP.VALIDATION_ERROR'));
    return;
  }
  isFetching.value = true;
  try {
    const response = await axios.get(`${apiBase()}/pages`, {
      params: {
        app_id: appId.value.trim(),
        app_secret: appSecret.value.trim(),
        short_lived_token: shortLivedToken.value.trim(),
      },
    });
    const data = response.data;
    pages.value = data.pages || [];
    userAccessToken.value = data.user_access_token || '';
    step.value = 2;
    if (pages.value.length === 0) {
      useAlert(t('INBOX_MGMT.ADD.FACEBOOK_APP.NO_PAGES'));
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.ADD.FACEBOOK_APP.API.FETCH_ERROR')
    );
  } finally {
    isFetching.value = false;
  }
};

const togglePage = pageId => {
  if (selectedPages.value[pageId]) {
    delete selectedPages.value[pageId];
  } else {
    selectedPages.value[pageId] = true;
  }
};

const selectedCount = () => Object.keys(selectedPages.value).length;

const connectPages = async () => {
  const selected = pages.value.filter(
    p => selectedPages.value[p.id] && !p.exists
  );
  if (selected.length === 0) {
    useAlert(t('INBOX_MGMT.ADD.FACEBOOK_APP.NO_SELECTION'));
    return;
  }
  isConnecting.value = true;
  try {
    const response = await axios.post(`${apiBase()}/connect`, {
      user_access_token: userAccessToken.value,
      pages: selected.map(p => ({
        id: p.id,
        name: p.name,
        access_token: p.access_token,
      })),
    });
    const data = response.data;
    const inboxes = data.inboxes || [];
    if (inboxes.length > 0) {
      router.replace({
        name: 'settings_inboxes_add_agents',
        params: { page: 'new', inbox_id: inboxes[0].id },
      });
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.ADD.FACEBOOK_APP.API.CONNECT_ERROR')
    );
  } finally {
    isConnecting.value = false;
  }
};

// ━━━ Token Management Logic ━━━
const checkTokenStatus = async () => {
  isChecking.value = true;
  try {
    const response = await axios.get(`${apiBase()}/token_status`);
    channels.value = response.data.channels || [];
    if (channels.value.length === 0) {
      useAlert(t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.NO_CHANNELS'));
    }
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.CHECK_ERROR')
    );
  } finally {
    isChecking.value = false;
  }
};

// Auto-check when switching to tokens tab
watch(activeTab, newTab => {
  if (newTab === 'tokens' && channels.value.length === 0) {
    checkTokenStatus();
  }
});

const renewAllTokens = async () => {
  if (!appId.value || !appSecret.value || !renewToken.value) {
    useAlert(t('INBOX_MGMT.ADD.FACEBOOK_APP.VALIDATION_ERROR'));
    return;
  }
  isRenewing.value = true;
  try {
    const response = await axios.post(`${apiBase()}/update_token`, {
      app_id: appId.value.trim(),
      app_secret: appSecret.value.trim(),
      short_lived_token: renewToken.value.trim(),
    });
    const count = response.data.count || 0;
    useAlert(
      t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.RENEW_SUCCESS', { count })
    );
    showRenewForm.value = false;
    renewToken.value = '';
    await checkTokenStatus();
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.RENEW_ERROR')
    );
  } finally {
    isRenewing.value = false;
  }
};

const tokenStatusClass = channel => {
  if (channel.never_expires) return 'bg-n-teal-2 text-n-teal-11';
  if (channel.is_valid) return 'bg-n-blue-2 text-n-blue-11';
  return 'bg-n-ruby-2 text-n-ruby-11';
};

const tokenStatusLabel = channel => {
  if (channel.never_expires)
    return t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.STATUS_NEVER_EXPIRES');
  if (channel.is_valid)
    return t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.STATUS_VALID');
  return t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.STATUS_EXPIRED');
};

const tokenStatusIcon = channel => {
  if (channel.never_expires) return '♾️';
  if (channel.is_valid) return '✅';
  return '❌';
};

const formatExpiry = channel => {
  if (channel.never_expires || !channel.expires_at_human) return '';
  const date = new Date(channel.expires_at_human);
  return date.toLocaleString();
};

const hasExpiredTokens = computed(() =>
  channels.value.some(c => !c.is_valid && !c.never_expires)
);
</script>

<template>
  <div class="w-full h-full col-span-6 p-6 overflow-auto">
    <!-- Tab Navigation -->
    <div class="flex gap-1 mb-6 border-b border-n-strong">
      <button
        class="px-4 py-2 text-sm font-medium transition-colors rounded-t-lg"
        :class="{
          'bg-woot-500 text-white': activeTab === 'add',
          'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-slate-2':
            activeTab !== 'add',
        }"
        @click="activeTab = 'add'"
      >
        {{ t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.TAB_ADD') }}
      </button>
      <button
        class="px-4 py-2 text-sm font-medium transition-colors rounded-t-lg"
        :class="{
          'bg-woot-500 text-white': activeTab === 'tokens',
          'text-n-slate-11 hover:text-n-slate-12 hover:bg-n-slate-2':
            activeTab !== 'tokens',
        }"
        @click="activeTab = 'tokens'"
      >
        {{ t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.TAB_TOKENS') }}
      </button>
    </div>

    <!-- ━━━ TAB: Add Pages ━━━ -->
    <div v-if="activeTab === 'add'">
      <!-- Step 1: Enter credentials -->
      <div v-if="step === 1" class="flex flex-col flex-wrap mx-0 max-w-lg">
        <div class="w-full">
          <PageHeader
            :header-title="t('INBOX_MGMT.ADD.FACEBOOK_APP.TITLE')"
            :header-content="t('INBOX_MGMT.ADD.FACEBOOK_APP.DESC')"
          />
        </div>
        <div class="w-full mt-4">
          <label>
            {{ t('INBOX_MGMT.ADD.FACEBOOK_APP.APP_ID.LABEL') }}
            <input
              v-model="appId"
              type="text"
              :placeholder="
                t('INBOX_MGMT.ADD.FACEBOOK_APP.APP_ID.PLACEHOLDER')
              "
            />
          </label>
        </div>
        <div class="w-full">
          <label>
            {{ t('INBOX_MGMT.ADD.FACEBOOK_APP.APP_SECRET.LABEL') }}
            <input
              v-model="appSecret"
              type="password"
              :placeholder="
                t('INBOX_MGMT.ADD.FACEBOOK_APP.APP_SECRET.PLACEHOLDER')
              "
            />
          </label>
        </div>
        <div class="w-full">
          <label>
            {{ t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN.LABEL') }}
            <textarea
              v-model="shortLivedToken"
              rows="3"
              :placeholder="
                t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN.PLACEHOLDER')
              "
              class="font-mono text-sm"
            />
          </label>
          <p class="text-xs text-n-slate-11 mt-1">
            {{ t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN.HELP') }}
          </p>
        </div>
        <div class="w-full text-right mt-4">
          <NextButton
            :label="t('INBOX_MGMT.ADD.FACEBOOK_APP.FETCH_PAGES')"
            :is-loading="isFetching"
            @click="fetchPages"
          />
        </div>
      </div>

      <!-- Step 2: Select pages -->
      <div v-if="step === 2" class="flex flex-col flex-wrap mx-0 max-w-lg">
        <div class="w-full">
          <PageHeader
            :header-title="t('INBOX_MGMT.ADD.FACEBOOK_APP.SELECT_TITLE')"
            :header-content="t('INBOX_MGMT.ADD.FACEBOOK_APP.SELECT_DESC')"
          />
        </div>
        <div class="w-full mt-4 flex flex-col gap-3">
          <div
            v-for="page in pages"
            :key="page.id"
            class="flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-all"
            :class="{
              'border-woot-500 bg-woot-25 dark:bg-woot-800/10':
                selectedPages[page.id],
              'border-n-weak hover:border-n-slate-6':
                !selectedPages[page.id],
              'opacity-50 cursor-not-allowed': page.exists,
            }"
            @click="!page.exists && togglePage(page.id)"
          >
            <img
              v-if="page.picture"
              :src="page.picture"
              :alt="page.name"
              class="size-10 rounded-full object-cover"
            />
            <div
              v-else
              class="size-10 rounded-full bg-n-slate-3 flex items-center justify-center"
            >
              <span class="text-sm font-medium text-n-slate-11">
                {{ page.name?.[0] }}
              </span>
            </div>
            <div class="flex-1 min-w-0">
              <p class="text-sm font-medium text-n-slate-12 truncate">
                {{ page.name }}
              </p>
              <p class="text-xs text-n-slate-11">ID: {{ page.id }}</p>
            </div>
            <span
              v-if="page.exists"
              class="text-xs px-2 py-1 rounded-full bg-n-amber-2 text-n-amber-11"
            >
              {{ t('INBOX_MGMT.ADD.FACEBOOK_APP.ALREADY_CONNECTED') }}
            </span>
            <div
              v-else
              class="size-5 rounded border-2 flex items-center justify-center transition-colors"
              :class="{
                'border-woot-500 bg-woot-500': selectedPages[page.id],
                'border-n-slate-6': !selectedPages[page.id],
              }"
            >
              <svg
                v-if="selectedPages[page.id]"
                class="size-3 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="3"
                  d="M5 13l4 4L19 7"
                />
              </svg>
            </div>
          </div>
        </div>
        <div class="w-full flex justify-between items-center mt-6">
          <button
            class="text-sm text-n-slate-11 hover:text-n-slate-12"
            @click="step = 1"
          >
            ← {{ t('INBOX_MGMT.ADD.FACEBOOK_APP.BACK') }}
          </button>
          <NextButton
            :label="
              t('INBOX_MGMT.ADD.FACEBOOK_APP.CONNECT_SELECTED', {
                count: selectedCount(),
              })
            "
            :is-loading="isConnecting"
            :is-disabled="selectedCount() === 0"
            @click="connectPages"
          />
        </div>
      </div>
    </div>

    <!-- ━━━ TAB: Token Management ━━━ -->
    <div v-if="activeTab === 'tokens'" class="flex flex-col max-w-2xl">
      <PageHeader
        :header-title="t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.TITLE')"
        :header-content="t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.DESC')"
      />

      <!-- Loading -->
      <div
        v-if="isChecking"
        class="mt-6 flex items-center gap-2 text-n-slate-11"
      >
        <span class="i-lucide-loader-2 animate-spin" />
        <span class="text-sm">Checking token status...</span>
      </div>

      <!-- No channels -->
      <div
        v-else-if="channels.length === 0 && !isChecking"
        class="mt-6 p-4 rounded-xl border border-n-weak bg-n-alpha-1 text-center"
      >
        <p class="text-sm text-n-slate-11">
          {{ t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.NO_CHANNELS') }}
        </p>
        <NextButton
          class="mt-3"
          :label="t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.CHECK_STATUS')"
          sm
          @click="checkTokenStatus"
        />
      </div>

      <!-- Token Status List -->
      <div v-else class="mt-6 flex flex-col gap-3">
        <div class="flex items-center justify-between mb-1">
          <p class="text-xs text-n-slate-11">
            {{ channels.length }} page(s) connected
          </p>
          <NextButton
            :label="t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.CHECK_STATUS')"
            sm
            ghost
            slate
            :is-loading="isChecking"
            @click="checkTokenStatus"
          />
        </div>

        <div
          v-for="channel in channels"
          :key="channel.page_id"
          class="flex items-center gap-4 p-4 rounded-xl border border-n-weak bg-n-alpha-1"
        >
          <div class="flex-1 min-w-0">
            <p class="text-sm font-semibold text-n-slate-12">
              {{ channel.inbox_name || 'Unknown' }}
            </p>
            <p class="text-xs text-n-slate-11 mt-0.5">
              Page ID: {{ channel.page_id }}
            </p>
            <p
              v-if="formatExpiry(channel)"
              class="text-xs text-n-slate-11 mt-0.5"
            >
              {{
                t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.EXPIRES_AT', {
                  date: formatExpiry(channel),
                })
              }}
            </p>
          </div>
          <span
            class="text-xs px-3 py-1.5 rounded-full font-medium flex items-center gap-1.5 whitespace-nowrap"
            :class="tokenStatusClass(channel)"
          >
            {{ tokenStatusIcon(channel) }}
            {{ tokenStatusLabel(channel) }}
          </span>
        </div>

        <!-- Renew Section -->
        <div class="mt-4 p-4 rounded-xl border border-n-weak bg-n-alpha-1">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-semibold text-n-slate-12">
                {{
                  t(
                    'INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.RENEW_TOKENS'
                  )
                }}
              </p>
              <p class="text-xs text-n-slate-11 mt-1">
                {{
                  t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.RENEW_DESC')
                }}
              </p>
            </div>
            <NextButton
              v-if="!showRenewForm"
              :label="
                t(
                  'INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.RENEW_TOKENS'
                )
              "
              sm
              @click="showRenewForm = true"
            />
          </div>
          <div v-if="showRenewForm" class="mt-4 flex flex-col gap-3">
            <div class="w-full">
              <label>
                {{ t('INBOX_MGMT.ADD.FACEBOOK_APP.APP_ID.LABEL') }}
                <input
                  v-model="appId"
                  type="text"
                  :placeholder="
                    t('INBOX_MGMT.ADD.FACEBOOK_APP.APP_ID.PLACEHOLDER')
                  "
                />
              </label>
            </div>
            <div class="w-full">
              <label>
                {{ t('INBOX_MGMT.ADD.FACEBOOK_APP.APP_SECRET.LABEL') }}
                <input
                  v-model="appSecret"
                  type="password"
                  :placeholder="
                    t(
                      'INBOX_MGMT.ADD.FACEBOOK_APP.APP_SECRET.PLACEHOLDER'
                    )
                  "
                />
              </label>
            </div>
            <div class="w-full">
              <label>
                {{ t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN.LABEL') }}
                <textarea
                  v-model="renewToken"
                  rows="3"
                  :placeholder="
                    t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN.PLACEHOLDER')
                  "
                  class="font-mono text-sm"
                />
              </label>
              <p class="text-xs text-n-slate-11 mt-1">
                {{ t('INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN.HELP') }}
              </p>
            </div>
            <div class="flex gap-2 mt-1">
              <NextButton
                :label="
                  t(
                    'INBOX_MGMT.ADD.FACEBOOK_APP.TOKEN_MGMT.RENEW_TOKENS'
                  )
                "
                :is-loading="isRenewing"
                @click="renewAllTokens"
              />
              <button
                class="text-sm text-n-slate-11 hover:text-n-slate-12 px-3"
                @click="showRenewForm = false"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
